import type { ResolveRequest, ResolvedScan, ScanIndex } from "@/api/order_check";
import type { OrderScan } from "@/lib/order-scan";

/** OrderScan → тело запроса резолва в 1С. null — скан не резолвится (напр. ШК-код склада). */
export function toResolveRequest(scan: OrderScan): ResolveRequest | null {
  switch (scan.kind) {
    case "ean13":
      return { scan_kind: "ean13", raw: scan.raw, ean13: scan.ean13 };
    case "gtin":
      return { scan_kind: "gtin", raw: scan.raw, gtin: scan.gtin };
    case "datamatrix":
      return { scan_kind: "datamatrix", raw: scan.raw, gtin: scan.gtin, serial: scan.serial };
    case "gs1":
      return { scan_kind: "gs1", raw: scan.raw, gtin: scan.gtin, batch: scan.batch, qty: scan.qty ?? null };
    case "sscc":
      // Коробка портала: состав знает бэк, локальным индексом её не резолвить.
      return { scan_kind: "sscc", raw: scan.raw };
    default:
      return null;
  }
}

/** Локальный резолв из предзагруженного индекса (без 1С). null → нужен бэкенд. */
export function resolveFromIndex(scan: OrderScan, index?: ScanIndex): ResolvedScan | null {
  if (!index) return null;
  const lookup = (code?: string): ResolvedScan | undefined => {
    if (!code) return undefined;
    return (
      index.entries[code] ??
      (code.length === 14 && code.startsWith("0") ? index.entries[code.slice(1)] : undefined)
    );
  };
  if (scan.kind === "ean13" || scan.kind === "gtin") {
    const e = lookup(scan.ean13 ?? scan.gtin);
    return e ? { ...e, scan_kind: scan.kind } : null;
  }
  // Коробка в предзагруженном индексе ШК заказа отсутствует по построению:
  // индекс — про товары 1С, а состав коробки знает портал.
  if (scan.kind === "sscc") return null;
  if (scan.kind === "gs1") {
    const e = lookup(scan.gtin);
    if (!e) return null;
    // Кол-во = коэффициент упаковки × AI30 (число упаковок в коде, обычно 1).
    // ⚠️ `qty_scanned` — само число упаковок, и его надо проставить здесь тоже:
    // бэк это делает, а локальный резолв оставлял единицу, то есть код с
    // «AI 30 = 2» записывался как «одна упаковка на 12 штук». Кода без AI 30
    // это касается наравне: нет поля — одна упаковка, а не ноль.
    // ⚠️ `||`, а не `??`: AI 30 может прийти НУЛЁМ (парсер принимает «0» как
    // законное число), и `??` пропустил бы его — позиция добавилась бы с нулём
    // штук, молча и мимо шторки. Бэк трактует ноль как «одна упаковка», и обе
    // стороны обязаны читать код одинаково.
    const packs = scan.qty || 1;
    const r: ResolvedScan = {
      ...e,
      scan_kind: "gs1",
      qty_scanned: packs,
      qty_base: e.unit_coefficient * packs,
    };
    const batch = scan.batch ?? "";
    if (batch) {
      const p = (index.partias[e.nomenclature_ref] ?? []).find((x) => x.name === batch);
      if (p) {
        r.partia_ref = p.ref;
        r.partia_name = p.name;
        r.partia_source = "auto";
        r.needs_partia = false;
      } else {
        // Партия скана не совпала с партиями заказа: чистим унаследованный ref,
        // имя = сканированная партия (подсказка), needs_partia → ручной ввод.
        r.partia_ref = "";
        r.partia_source = "";
        r.partia_name = batch;
        r.needs_partia = true;
      }
    }
    return r;
  }
  return null; // ЧЗ-datamatrix — всегда через 1С (serial уникален)
}
